import { useState } from "react";

import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { FilterSearchBox } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterSearchBox/FilterSearchBox";
import type { FilterSearchConfigMap } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/filter-search-service/types";
import type { IFiltersConfigCategoryItem } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/category";
import type { FiltersConfigStatus } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import { SelectedCriteriaCompaniesCount } from "app/pages/sales-intelligence/modules/leads-search/components/SelectedCriteriaCompaniesCount";
import { SelectedFiltersSidebar } from "app/pages/sales-intelligence/modules/leads-search-filters/components/selected-filters-sidebar/SelectedFiltersSidebar";
import type { LeadsSearchFilterInstance } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilterInstance";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";

import { ScoringFiltersPanel } from "../ScoringFiltersPanel";
import { StyledScoringWizardDivider } from "../styles";
import { WizardStepHeading } from "../WizardStepHeading";

type Props = {
    panelValue: Partial<LeadsSearchAllFiltersTypes>;
    onPanelValueUpdate: (next: Partial<LeadsSearchAllFiltersTypes>) => void;
    filtersList: readonly LeadsSearchFilterInstance[];
    filtersSearchConfigList: readonly FilterSearchConfigMap[];
    categoriesList: readonly IFiltersConfigCategoryItem[];
    status: FiltersConfigStatus;
};

export const Step1FiltersAndSignals = ({
    panelValue,
    onPanelValueUpdate,
    filtersList,
    filtersSearchConfigList,
    categoriesList,
    status,
}: Props) => {
    // Owned here so the search input can sit next to the heading on a single row, while the
    // panel below consumes the same query for its filter list.
    const [searchQuery, setSearchQuery] = useState("");

    return (
        <Stack
            flex="1"
            minHeight="0"
            gap="16px"
            data-automation="scoring-rule-wizard.step-filters"
        >
            <Stack
                gap="0"
                flexShrink={0}
            >
                <Flex
                    gap="16px"
                    justifyContent="space-between"
                    alignItems="center"
                    padding="0 0 16px"
                >
                    <WizardStepHeading
                        headingKey="si.pages.score_settings.wizard.step1.heading"
                        descriptionKey="si.pages.score_settings.wizard.step1.description"
                    />
                    <FilterSearchBox onChange={setSearchQuery} />
                </Flex>
                <StyledScoringWizardDivider />
            </Stack>

            <Flex
                flex="1"
                minHeight="0"
                gap="16px"
                alignItems="stretch"
            >
                <Stack
                    flex="1"
                    minWidth="0"
                    minHeight="0"
                    gap="12px"
                >
                    <ScoringFiltersPanel
                        panelValue={panelValue}
                        onPanelValueUpdate={onPanelValueUpdate}
                        searchQuery={searchQuery}
                        filtersList={filtersList}
                        filtersSearchConfigList={filtersSearchConfigList}
                        categoriesList={categoriesList}
                        status={status}
                    />
                </Stack>

                <SelectedFiltersSidebar
                    panelValue={panelValue}
                    onPanelValueUpdate={onPanelValueUpdate}
                    filtersList={filtersList}
                    translationKeys={{
                        label: "si.pages.score_settings.wizard.step1.selected.label",
                        empty: "si.pages.score_settings.wizard.step1.selected.empty",
                    }}
                    renderCompaniesCount={({ panelValue: value, isEnabled, dataAutomationPrefix }) => (
                        <SelectedCriteriaCompaniesCount
                            panelValue={value}
                            isEnabled={isEnabled}
                            dataAutomationPrefix={dataAutomationPrefix}
                            companiesCountTranslationKey="si.pages.score_settings.wizard.step1.selected.companies_count"
                        />
                    )}
                />
            </Flex>
        </Stack>
    );
};
